<template>
    <b-modal :id="id" size="xl" @ok="handleOk" @deleteShipmentPackaging="deleteShipmentPackaging">
        <template v-slot:modal-title>
            {{ $t('editShipmentPackaging.modal.title') }}
            <font-awesome-icon icon="edit"></font-awesome-icon>
        </template>

        <form ref="form" @submit.stop.prevent="handleOk">

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.shipmentPackagings')">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <vSelect :dynamicWidth="true"
                                 label="productName"
                                 :placeholder="'Choose Shipment Packaging'"
                                 :options="listOfCustomerProducts"
                                 v-model="selectedCustomerProduct" />
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.name')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="text"
                                      v-model="shipmentPackaging.productName"
                                      :name="'shipmentPackaging.productName'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.productName.$error }" />
                        <b-form-invalid-feedback id="productName-feedback"
                                                 :name="'shipmentPackaging.productName'"
                                                 v-if="$v.shipmentPackaging.productName.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.name"
                                                :fieldValid="$v.shipmentPackaging.productName.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.number')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="text"
                                      v-model="shipmentPackaging.number"
                                      :name="'shipmentPackaging.number'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.number.$error }" />
                        <b-form-invalid-feedback id="number-feedback"
                                                 :name="'shipmentPackaging.number'"
                                                 v-if="$v.shipmentPackaging.number.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.number"
                                                :fieldValid="$v.shipmentPackaging.number.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.harmonizedCode')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="text"
                                      v-model="shipmentPackaging.harmonizedCode"
                                      :name="'shipmentPackaging.harmonizedCode'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.harmonizedCode.$error }" />
                        <b-form-invalid-feedback id="harmonizedCode-feedback"
                                                 :name="'shipmentPackaging.harmonizedCode'"
                                                 v-if="$v.shipmentPackaging.harmonizedCode.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.harmonizedCode"
                                                :fieldValid="$v.shipmentPackaging.harmonizedCode.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.commodity')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="text"
                                      v-model="shipmentPackaging.commodity"
                                      :name="'shipmentPackaging.commodity'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.commodity.$error }" />
                        <b-form-invalid-feedback id="commodity-feedback"
                                                 :name="'shipmentPackaging.commodity'"
                                                 v-if="$v.shipmentPackaging.commodity.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.commodity"
                                                :fieldValid="$v.shipmentPackaging.commodity.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.countryOfOrigin')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <vSelect :dynamicWidth="true"
                                 :options="listOfCountries"
                                 :placeholder="$t('editShipmentPackaging.placeholder.chooseCountry')"
                                 :reduce="(country) => country.countryCode"
                                 label="countryName"
                                 v-model="shipmentPackaging.countryOfOrigin"
                                 :name="'shipmentPackaging.countryOfOrigin'"
                                 :select-on-tab="true"
                                 :class="{ 'is-invalid': $v.shipmentPackaging.countryOfOrigin.$error }" />
                        <b-form-invalid-feedback id="countryOfOrigin-feedback"
                                                 :name="'shipmentPackaging.countryOfOrigin'"
                                                 :state="!$v.shipmentPackaging.countryOfOrigin.$error"
                                                 v-if="$v.shipmentPackaging.countryOfOrigin.$dirty">
                            <validationRequired fieldName="editShipmentPackaging.label.countryOfOrigin"
                                                :fieldValid="$v.shipmentPackaging.countryOfOrigin.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.unitPrice')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.unitPrice"
                                      :name="'shipmentPackaging.unitPrice'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.unitPrice.$error }" />
                        <b-form-invalid-feedback id="unitPrice-feedback"
                                                 :name="'shipmentPackaging.unitPrice'"
                                                 v-if="$v.shipmentPackaging.unitPrice.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.unitPrice"
                                                :fieldValid="$v.shipmentPackaging.unitPrice.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.currency')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <vSelect :dynamicWidth="true"
                                 :options="getListOfInsuranceCurrencies"
                                 :select-on-tab="true"
                                 :placeholder="$t('editShipmentPackaging.placeholder.chooseCurrency')"
                                 :reduce="(currency) => currency.currencyCode"
                                 label="currencyName"
                                 v-model="shipmentPackaging.currency"
                                 name="shipmentPackaging.currency"
                                 :class="{ 'is-invalid': $v.shipmentPackaging.currency.$error }" />
                        <b-form-invalid-feedback id="currency-feedback"
                                                 :name="'shipmentPackaging.currency'"
                                                 v-if="$v.shipmentPackaging.currency.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.currency"
                                                :fieldValid="$v.shipmentPackaging.currency.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.weight')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.weight"
                                      :name="'shipmentPackaging.weight'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.weight.$error }" />
                        <b-form-invalid-feedback id="weight-feedback"
                                                 :name="'shipmentPackaging.weight'"
                                                 v-if="$v.shipmentPackaging.weight.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.weight"
                                                :fieldValid="$v.shipmentPackaging.weight.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.length')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.length"
                                      :name="'shipmentPackaging.length'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.length.$error }" />
                        <b-form-invalid-feedback id="length-feedback"
                                                 :name="'shipmentPackaging.length'"
                                                 v-if="$v.shipmentPackaging.length.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.length"
                                                :fieldValid="$v.shipmentPackaging.length.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.width')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.width"
                                      :name="'shipmentPackaging.width'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.width.$error }" />
                        <b-form-invalid-feedback id="width-feedback"
                                                 :name="'shipmentPackaging.width'"
                                                 v-if="$v.shipmentPackaging.width.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.width"
                                                :fieldValid="$v.shipmentPackaging.width.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.height')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.height"
                                      :name="'shipmentPackaging.height'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.height.$error }" />
                        <b-form-invalid-feedback id="height-feedback"
                                                 :name="'shipmentPackaging.height'"
                                                 v-if="$v.shipmentPackaging.height.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.height"
                                                :fieldValid="$v.shipmentPackaging.height.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.dryIceWeight')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <b-form-input size="sm"
                                      type="number"
                                      step="0.01"
                                      v-model="shipmentPackaging.dryIceWeight"
                                      :name="'shipmentPackaging.dryIceWeight'"
                                      :class="{ 'is-invalid': $v.shipmentPackaging.dryIceWeight.$error }" />
                        <b-form-invalid-feedback id="dryIceWeight-feedback"
                                                 :name="'shipmentPackaging.dryIceWeight'"
                                                 v-if="$v.shipmentPackaging.dryIceWeight.$error">
                            <validationRequired fieldName="editShipmentPackaging.label.dryIceWeight"
                                                :fieldValid="$v.shipmentPackaging.dryIceWeight.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-group label-cols-sm="3"
                          :label="$t('editShipmentPackaging.label.packagingType')"
                          label-align-sm="left">
                <b-input-group size="sm">
                    <b-col sm="11">
                        <vSelect :dynamicWidth="true"
                                 :options="listOfPackagingTypes"
                                 :select-on-tab="true"
                                 label="name"
                                 :name="'shipmentPackaging.packageType.name'"
                                 :placeholder="$t('editShipmentPackaging.placeholder.choosePackagingType')"
                                 :reduce="(p) => p.packageType"
                                 v-model="shipmentPackaging.packageType"
                                 v-if="listOfPackagingTypes.length > 0"
                                 :clearable="false"
                                 :class="{ 'is-invalid': $v.shipmentPackaging.packageType.$error }" />
                        <b-form-invalid-feedback id="packagingType-feedback"
                                                 :name="'shipmentPackaging.packageType'"
                                                 :state="!$v.shipmentPackaging.packageType.$error"
                                                 v-if="$v.shipmentPackaging.packageType.$dirty">
                            <validationRequired fieldName="editShipmentPackaging.label.packagingType"
                                                :fieldValid="$v.shipmentPackaging.packageType.required" />
                        </b-form-invalid-feedback>
                    </b-col>
                </b-input-group>
            </b-form-group>

            <b-form-invalid-feedback name="editShipmentPackagingInvalidFeedback"
                                     v-if="invalidFeedback"
                                     :state="!state">{{ invalidFeedback }}</b-form-invalid-feedback>
        </form>

        <template v-slot:modal-footer="{ ok }">
            <b-button v-if="shipmentPackaging.id"
                      @click="displayConfirmModal()"
                      variant="primary"
                      name="deleteShipmentPackagingButton">
                <font-awesome-icon icon="save" />
                {{ $t('editShipmentPackaging.button.deleteShipmentPackaging') }}
            </b-button>
            <b-button v-if="shipmentPackaging.id"
                      @click="ok()"
                      variant="primary"
                      name="updateShipmentPackagingButton">
                <font-awesome-icon icon="save" />
                {{ $t('editShipmentPackaging.button.updateShipmentPackaging') }}
            </b-button>
            <b-button v-if="!shipmentPackaging.id"
                      @click="ok()"
                      variant="primary"
                      name="addShipmentPackagingButton">
                <font-awesome-icon icon="save" />
                {{ $t('editShipmentPackaging.button.addShipmentPackaging') }}
            </b-button>
        </template>

        <confirmModal ref="deleteShipmentPackagingConfirmModal"
                      @confirmAction="deleteShipmentPackaging"
                      okButtonName="deleteShipmentPackagingConfirmButton" />
    </b-modal>
</template>

<script>
    import { mapGetters } from 'vuex';
    import axios from 'axios';
    import vSelect from './vSelect';
    import { ACTIONS, MUTATIONS, CREATE_SHIPMENT_PREFIX, GETTERS } from '../store/constants';
    import validationRequired from './Validation/ValidationRequired';
    import validationMaxLength from './Validation/ValidationMaxLength';
    import shipmentPackagingValidation from '../validators/shipmentPackagingValidation';
    import confirmModal from './confirmModal';
    import _ from 'lodash';

    export default {
        components: {
            vSelect,
            confirmModal,
            shipmentPackagingEdit: () => import('./numberOfPackages'),
            validationRequired,
            validationMaxLength
        },

        validations: {
            shipmentPackaging: shipmentPackagingValidation,
        },

        props: {
            refPrefix: {
                type: String,
            },
            shipmentPackagingToEdit: {
                type: Object,
                default: null,
            },
        },

        watch: {
            shipmentPackagingToEdit: {
                handler() {
                    if (!this.shipmentPackagingToEdit) {
                        this.resetData();
                        return;
                    }
                    this.shipmentPackaging = this.shipmentPackagingToEdit;
                    this.selectedCustomerProduct = this.shipmentPackagingToEdit;
                },
            },
            getSelectedCompanyId: {
                handler() {
                    this.shipmentPackaging.companyId = this.getSelectedCompanyId;
                },
            },
            selectedCustomerProduct: {
                handler() {
                    if (this.selectedCustomerProduct) {
                        this.shipmentPackaging = this.selectedCustomerProduct;
                    } else {
                        this.resetData();
                    }
                },
            },
        },

        data() {
            return {
                shipmentPackaging: null,
                invalidFeedback: '',
                selectedCustomerProduct: null,
            };
        },

        computed: {
            validation() {
                return this.$v.shipmentPackaging;
            },
            state() {
                return this.$v.$anyError;
            },
            listOfPackagingTypes() {
                return this.$store.state.createShipment.packagingTypes;
            },
            listOfCountries() {
                return this.$store.state.core.countries;
            },
            getListOfInsuranceCurrencies() {
                return this.$store.state.core.currencies;
            },
            listOfCustomerProducts() {
                return this.$store.getters[
                    CREATE_SHIPMENT_PREFIX + GETTERS.CREATE_SHIPMENT.SHIPMENTPACKAGING
                ];
            },
            ...mapGetters('createShipment', ['getSelectedCompanyId', 'getPrototype'])
        },
        methods: {
            handleOk(bvModalEvt) {
                bvModalEvt.preventDefault();
                if (this.$v.$anyError) {
                    return;
                }

                let shipmentPackagingClone = this.prepareData();
                this.$store.dispatch(
                    CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.UPSERT_SHIPMENTPACKAGING,
                    shipmentPackagingClone
                );
                this.$emit('reloadProducts');
                this.$nextTick(() => {
                    this.$bvModal.hide(this.id);
                });
                this.resetData();
            },
            deleteShipmentPackaging() {
                let shipmentPackagingClone = this.prepareData();
                this.$store.dispatch(
                    CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.DELETE_SHIPMENTPACKAGING,
                    shipmentPackagingClone
                );
                this.selectedCustomerProduct = null;

                this.$emit('reloadProducts');
                this.$bvModal.hide(this.id);
            },
            prepareData() {
                this.shipmentPackaging.unitPrice = parseFloat(this.shipmentPackaging.unitPrice);
                let x = _.find(this.listOfPackagingTypes, { packageType: this.shipmentPackaging.packageType });
                let shipmentPackagingClone = _.cloneDeep(this.shipmentPackaging);
                shipmentPackagingClone.packageType = x ? x.packageType : this.listOfPackagingTypes[0].packageType;
                return shipmentPackagingClone;
            },
            displayConfirmModal() {
                this.$refs['deleteShipmentPackagingConfirmModal'].show();
            },
            resetData() {
                var companyId = this.getSelectedCompanyId;
                this.shipmentPackaging = {
                    productName: null,
                    packageType: null,
                    number: null,
                    harmonizedCode: null,
                    commodity: null,
                    countryOfOrigin: null,
                    unitPrice: null,
                    currency: null,
                    weight: null,
                    length: null,
                    width: null,
                    height: null,
                    companyId: companyId,
                    dryIceWeight: 0,
                };
                // Set package type to "Other" by default
                if (this.listOfPackagingTypes.length > 0) {
                    this.shipmentPackaging.packageType = this.listOfPackagingTypes[0];
                }
                this.selectedCustomerProduct = null;
            },
        },

        created() {
            this.id = 'editModal-shipmentPackaging';
            this.resetData();
            // Touch all fields to show validation errors
            this.$v.shipmentPackaging.productName.$touch();
            this.$v.shipmentPackaging.harmonizedCode.$touch();
            this.$v.shipmentPackaging.number.$touch();
            this.$v.shipmentPackaging.commodity.$touch();
            this.$v.shipmentPackaging.countryOfOrigin.$touch();
            this.$v.shipmentPackaging.unitPrice.$touch();
            this.$v.shipmentPackaging.currency.$touch();
            this.$v.shipmentPackaging.weight.$touch();
            this.$v.shipmentPackaging.length.$touch();
            this.$v.shipmentPackaging.width.$touch();
            this.$v.shipmentPackaging.height.$touch();
            this.$v.shipmentPackaging.dryIceWeight.$touch();
            this.$v.shipmentPackaging.packageType.$touch();
        },
    };
</script>
